<template>
  <label
    v-click-outside="onBlur"
    class="text-input"
    :for="id"
    :class="textInputClass"
  >
    <div class="text-input__icon">
      <slot name="icon" />
    </div>
    <div class="text-input__field">
      <input
        v-if="masked"
        :id="id"
        v-maska="masked"
        class="text-input__input"
        :value="value"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :autocomplete="autocomplete"
        :[typeAttr]="type"
        @input="$emit('onInput', $event.target?.value)"
        @focus="onFocus"
        @focusout="onFocusout"
      />
      <input
        v-else
        :id="id"
        class="text-input__input"
        :value="value"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :autocomplete="autocomplete"
        :[typeAttr]="type"
        @input="$emit('onInput', $event.target?.value)"
        @focus="onFocus"
        @focusout="onFocusout"
      />
      <div class="text-input__actions">
        <slot />
      </div>
    </div>
  </label>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';
  import { vMaska } from 'maska/vue';

  export default defineNuxtComponent({
    name: 'TextInputCustom',

    directives: {
      maska: vMaska,
      clickOutside: vOnClickOutside,
    },

    props: {
      id: {
        type: String as PropType<string>,
        required: true,
      },

      value: {
        required: true,
        validator: (value: unknown) => {
          return typeof value === 'string' || value === null;
        },
      },

      placeholder: {
        type: String as PropType<string>,
        default: '',
      },

      maxlength: {
        type: Number as PropType<number>,
        default: -1,
      },

      autocomplete: {
        type: String as PropType<string>,
        default: 'off',
      },

      state: {
        type: String as PropType<string>,
        default: '',
      },

      type: {
        type: String as PropType<string>,
        default: '',
      },

      masked: {
        type: String as PropType<string>,
        default: '',
      },
    },

    emits: ['onInput', 'on-focus', 'on-blur', 'on-focusout'],

    computed: {
      textInputClass() {
        return {
          [`text-input--${this.state}`]: this.state,
        };
      },

      typeAttr() {
        return this.type ? 'type' : '';
      },
    },

    methods: {
      onFocus(): void {
        this.$emit('on-focus');
      },

      onBlur(): void {
        this.$emit('on-blur');
      },

      onFocusout() {
        this.$emit('on-focusout');
      },
    },
  });
</script>

<style lang="scss" scoped>
  .text-input {
    $textInput: &;

    cursor: pointer;
    background-color: #fff;
    display: flex;

    @include hover {
      #{$textInput} {
        &__field {
          border-color: #c722ca;
        }
      }
    }

    &--error {
      #{$textInput} {
        &__input {
          color: #ed0000;
        }

        &__field {
          border-color: #ed0000;
        }
      }
    }

    &__input {
      z-index: 5;
      width: 100%;
      padding: 12px 16px;
      font-family: $text-font--1;
      @include normalText;
      resize: none;
      border: none;
      border-radius: 8px;
      outline: none;
      cursor: pointer;
    }

    &__field {
      display: flex;
      border: 1px solid #e7e7e7;
      border-radius: 8px;
      width: 100%;
    }

    &__actions {
      width: 48px;
      height: 48px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
</style>
